import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormatting } from 'locale';

import {
    NumberInput, Title, Infobox, RadioButtonGroup,
    Checkbox,
} from 'ui-library';

import { ICON_INFO_OUTLINE } from 'ui-library/components/Icon';
import { DEFAULT_APP_CURRENCY } from 'constants/constants';
import IconWithTooltip from 'components/common/IconWithTooltip';

import './TypeOfOrder.css';

export const TYPE_OF_ORDER_TITLES = {
    1: 'createNewCase.TypeOfOrder.Option.Partial.Label',
    2: 'createNewCase.TypeOfOrder.Option.Full.Label',
};

const TypeOfOrderOptionLabelPartial = (t, amount, isPK) => (
    <div className="type-of-order-option-label">
        <span>{t(TYPE_OF_ORDER_TITLES[1])}</span>
        <span className="type-of-order-option-label--description">
            {isPK ? t('createNewCase.TypeOfOrder.Option.Partial.PK') : t('createNewCase.TypeOfOrder.Option.Partial.Description', { amount })}
        </span>
    </div>
);
const TypeOfOrderOptionLabelFull = (t, isPK) => (
    <div className="type-of-order-option-label">
        <span>{t(TYPE_OF_ORDER_TITLES[2])}</span>
        <span className="type-of-order-option-label--description">
            {isPK ? t('createNewCase.TypeOfOrder.Option.Full.PK') : t('createNewCase.TypeOfOrder.Option.Full.Description')}
        </span>
    </div>
);

const TypeOfOrder = ({ datasource }) => {
    const { t } = useTranslation();
    const {
        data,
        control,
        errors,
        trigger,
        currentDepotValue,
        maxWithdrawalAmount,
        hasPendingSalesOrder,
        handleTypeOfOrder,
        isPOA,
        isPA,
        isPK,
        mainOwner,
    } = datasource();
    const { getFormattedCurrency } = useFormatting();

    const mainClientsName = useMemo(() => ({
        firstName: mainOwner?.name?.split(' ')?.[0] || '',
        lastName: mainOwner?.name?.split(' ')?.[1] || '',
    }), [mainOwner?.name]);

    const getInfoMessage = () => {
        if (isPA) {
            return 'createNewCase.TypeOfOrder.infoBoxText.PA';
        }

        return 'createNewCase.TypeOfOrder.infoBoxText';
    };

    return (
        <form className="type-of-order-form">
            <Title type="3" className="type-or-order--amount-table">
                <div className="type-or-order--current-depot-value--title">
                    {t('createNewCase.TypeOfOrder.currentDepotValue')}
                    {': '}
                    {hasPendingSalesOrder && <IconWithTooltip description={t('createNewCase.TypeOfOrder.currentDepotValue.Info')} />}
                </div>
                <span>
                    {DEFAULT_APP_CURRENCY}
                    {' '}
                    <strong>
                        {getFormattedCurrency(currentDepotValue || 0, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                            signDisplay: 'never',
                        })}
                    </strong>
                </span>
            </Title>

            <div className="types-of-order">
                <RadioButtonGroup
                    value={data?.OrderTypeId}
                    options={[
                        {
                            label: TypeOfOrderOptionLabelPartial(t, getFormattedCurrency(maxWithdrawalAmount || 0, {
                                currency: DEFAULT_APP_CURRENCY,
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                                signDisplay: 'never',
                            }), isPK),
                            value: '1',
                        },
                        {
                            label: TypeOfOrderOptionLabelFull(t, isPK),
                            value: '2',
                            disabled: isPOA && !isPA,
                        },
                    ]}
                    onChange={(e) => handleTypeOfOrder(e.target.value)}
                />
            </div>

            <div className="type-of-order--amount">
                <Controller
                    name="Amount"
                    control={control}
                    render={({ field: { value, onChange, ref, ...field } }) => (
                        <NumberInput
                            {...field}
                            data-cy="Amount"
                            name="Amount"
                            label={t('createNewCase.TypeOfOrder.withdrawalAmount')}
                            value={value || 0}
                            error={t(errors?.Amount?.message, {
                                amount: getFormattedCurrency(maxWithdrawalAmount, {
                                    currency: DEFAULT_APP_CURRENCY,
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2,
                                    signDisplay: 'never',
                                }),
                            })}
                            onChange={(e) => {
                                onChange(e);
                                trigger('Amount');
                            }}
                            thousandSeparator="’"
                            decimalSeparator="."
                            prefix={`${DEFAULT_APP_CURRENCY} `}
                            required
                            allowNegativeValue={false}
                            textRight
                            disabled={data?.OrderTypeId === '2'}
                        />
                    )}
                />
            </div>

            {isPK && data?.OrderTypeId === '2'
                && (
                    <div className="mt-30">
                        <Controller
                            name="NeedCloseAccount"
                            control={control}
                            render={({ field: { value, ref, ...field } }) => (
                                <Checkbox
                                    {...field}
                                    checked={value}
                                    label={t('createNewCase.TypeOfOrder.PrivateAccount.CloseAccount')}
                                />
                            )}
                        />
                    </div>

                )}

            <Title type="3" className="type-or-order--amount-table">
                {t('createNewCase.TypeOfOrder.remainingAmount')}
                {': '}
                <span>
                    {currentDepotValue < data?.Amount ? '-' : ''}
                    {DEFAULT_APP_CURRENCY}
                    {' '}
                    <strong>
                        {getFormattedCurrency(data?.OrderTypeId !== '2' ? (currentDepotValue - (data?.Amount || 0)) : 0, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                            signDisplay: 'never',
                        })}
                    </strong>
                </span>
            </Title>

            <Infobox icon={ICON_INFO_OUTLINE}>
                {t(getInfoMessage(), { firstName: mainClientsName?.firstName, lastName: mainClientsName?.lastName })}
            </Infobox>
        </form>
    );
};

export default TypeOfOrder;
